import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

/**
 * Shared folder + selection state for library pages (Persons, Logos).
 *
 * @param {Object} cfg
 * @param {string} cfg.folderType   - Backend folder_type ("persons" | "logos")
 * @param {string} cfg.itemEndpoint - REST path for the items themselves
 *                                    (e.g. "/persons" or "/logos") — used for
 *                                    bulk-moving items to folders.
 * @param {Function} cfg.setItems   - Setter to keep the items list in sync when
 *                                    folders/items are mutated (delete folder,
 *                                    move item, bulk move). Receives the same
 *                                    callback signature as a React setState.
 * @param {string}  [cfg.itemNounSingular="Item"]
 * @param {string}  [cfg.itemNounPlural="Items"]
 */
export function useFolders({
  folderType,
  itemEndpoint,
  setItems,
  itemNounSingular = "Item",
  itemNounPlural = "Items",
}) {
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("all");
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");

  const currentUser = getUser();

  const fetchFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/folders?folder_type=${folderType}`);
      setFolders(res.data || []);
    } catch (err) {
      console.error("Error fetching folders:", err);
    }
  }, [folderType]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/folders`, { name, folder_type: folderType });
      setFolders((prev) => [
        ...prev,
        { id: res.data.id, name, folder_type: folderType, owner_id: currentUser?.id, shared_with_org: false },
      ]);
      setNewFolderName("");
      setShowNewFolder(false);
      setSelectedFolderId(res.data.id);
      toast.success(`Ordner "${name}" erstellt`);
    } catch {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleRenameFolder = async (folderId) => {
    const name = editFolderName.trim();
    if (!name) {
      setEditingFolderId(null);
      return;
    }
    try {
      await axios.put(`${API}/folders/${folderId}`, { name });
      setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, name } : f)));
      toast.success("Ordner umbenannt");
    } catch {
      toast.error("Fehler beim Umbenennen");
    } finally {
      setEditingFolderId(null);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      // Items previously assigned to this folder are now unassigned
      setItems?.((prev) => prev.map((it) => (it.folder_id === folderId ? { ...it, folder_id: null } : it)));
      if (selectedFolderId === folderId) setSelectedFolderId("all");
      toast.success("Ordner gelöscht");
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleShareFolder = async (folder) => {
    try {
      if (folder.shared_with_org) {
        await axios.delete(`${API}/folders/${folder.id}/share`);
      } else {
        await axios.post(`${API}/folders/${folder.id}/share`);
      }
      setFolders((prev) =>
        prev.map((f) => (f.id === folder.id ? { ...f, shared_with_org: !f.shared_with_org } : f))
      );
      toast.success(folder.shared_with_org ? "Freigabe aufgehoben" : "Ordner geteilt");
    } catch {
      toast.error("Fehler");
    }
  };

  const handleMoveToFolder = async (itemId, folderId) => {
    try {
      await axios.put(`${API}${itemEndpoint}/${itemId}`, { folder_id: folderId || "" });
      setItems?.((prev) => prev.map((it) => (it.id === itemId ? { ...it, folder_id: folderId || null } : it)));
    } catch {
      toast.error("Fehler beim Verschieben");
    }
  };

  const handleBulkMoveToFolder = async (folderId, selectedIds, onSuccess) => {
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => axios.put(`${API}${itemEndpoint}/${id}`, { folder_id: folderId || "" })));
      setItems?.((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, folder_id: folderId || null } : it)));
      onSuccess?.();
      toast.success(`${ids.length} ${ids.length === 1 ? itemNounSingular : itemNounPlural} verschoben`);
    } catch {
      toast.error("Fehler beim Verschieben");
    }
  };

  /**
   * Filter helper: applies folder filter ("all" | "none" | folderId) to a list.
   */
  const filterByFolder = (items) => {
    if (selectedFolderId === "all") return items;
    if (selectedFolderId === "none") return items.filter((it) => !it.folder_id);
    return items.filter((it) => it.folder_id === selectedFolderId);
  };

  return {
    folders,
    setFolders,
    selectedFolderId,
    setSelectedFolderId,
    newFolderName,
    setNewFolderName,
    showNewFolder,
    setShowNewFolder,
    editingFolderId,
    setEditingFolderId,
    editFolderName,
    setEditFolderName,
    currentUser,
    fetchFolders,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleToggleShareFolder,
    handleMoveToFolder,
    handleBulkMoveToFolder,
    filterByFolder,
  };
}
